/* eslint-disable max-lines */
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { mapSeniorityType } from 'services/contacts/seniorityTypes';
import pluralize from 'pluralize';
import { reverseMapOrganizationHeadCount } from 'helpers/organizations';
import { mapStageToFund } from 'components/organisms/contactDetail/contactInfoWorkDate';
import { useSecurePdfOpener } from 'hooks/useSecurePdfOpener';
import { addProtocolToUrl } from 'helpers/urlHelpers';
import { contactsSortOptions } from 'components/organisms/contactsFilters/defaultFilters';
import { jobApplicantsJobTnSelector, userSelector } from 'redux/selectors';
import { createTableColumn as createCustomFieldColumn } from 'services/customFieldsV2';

export const getContactHeader = (contactCount) =>
  `${contactCount?.toLocaleString()} ${pluralize('contact', contactCount ?? 0)}`;

export const getColumns = ({ type, listId, contactCount, tnHidden, customFields, joinTnFromJB } = {}) => {
  const disableOneSharedList = new Set([
    'workEmail',
    'personalEmail',
    'list',
    'tags',
    'addedBy',
    'signupDate',
    'preferredJobFunction',
    'levelOfExperience',
    'preferredLocation',
    'workEnvironment',
    'jobSearchStatus',
    'resume',
    'jobAlerts',
    'jobAlertsKeywords',
    'website',
    'talentNetworkSignup',
  ]);

  const tnHiddenColumns = new Set([
    'talentNetworkSignup',
    'signupDate',
    'preferredJobFunction',
    'levelOfExperience',
    'preferredLocation',
    'workEnvironment',
    'jobSearchStatus',
    'resume',
    'jobAlerts',
    'jobAlertsKeywords',
    'website',
  ]);

  const hideNewTnColumns = new Set(['jobApplications']);
  const customFieldsColumns = customFields.map((field) => createCustomFieldColumn(field));

  return [
    {
      Header: getContactHeader(contactCount),
      accessor: 'name',
      width: 300,
      hide: false,
      pinned: 'left',
      cellRenderer: 'contactNameRenderer',
      suppressMovable: true,
      disableSortBy: false,
      show: true,
      hideOnTableColumn: true,
      valueFormatter: ({ data: { original } }) => original.fullName,
    },
    {
      Header: 'Current job title',
      accessor: 'jobTitle',
      disableSortBy: false,
      width: 300,
      cellRenderer: 'contactCompanyRenderer',
      show: true,
      valueFormatter: ({ data: { jobTitle } }) =>
        jobTitle?.experiences?.map((exp) => exp[jobTitle.columnKey]).join(', ') ?? '',
    },
    {
      Header: 'Current company',
      accessor: 'company',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactCompanyRenderer',
      show: true,
      valueFormatter: ({ data: { company } }) =>
        company?.experiences?.map((exp) => exp[company.columnKey]).join(', ') ?? '',
    },
    {
      Header: 'Contact of',
      accessor: 'contactOf',
      width: 280,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactOfRenderer',
      show: true,
      valueFormatter: ({ data: { contactOf } }) => contactOf?.map((of) => of.name).join(', ') ?? '',
    },
    {
      Header: 'Current company size',
      accessor: 'companySize',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Current company  funding stage',
      accessor: 'companyFunding',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'LinkedIn profile',
      accessor: 'linkedinProfile',
      width: 300,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactLinkRenderer',
      show: true,
    },
    {
      Header: 'LinkedIn tagline',
      accessor: 'linkedinTagline',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Work email',
      accessor: 'workEmail',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactEmailRenderer',
      show: true,
      valueFormatter: ({ value }) => value?.join(', ') ?? '',
    },
    {
      Header: 'Personal email',
      accessor: 'personalEmail',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactEmailRenderer',
      show: true,
      valueFormatter: ({ value }) => value?.join(', ') ?? '',
    },
    {
      Header: 'Location',
      accessor: 'location',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Seniority',
      accessor: 'seniority',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Years of experience',
      accessor: 'yearsOfExperience',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Years in current company',
      accessor: 'yearsInCurrentCompany',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Industries',
      accessor: 'industry',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Lists',
      accessor: 'list',
      width: 280,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactListRenderer',
      show: true,
      valueFormatter: ({ data: { list } }) => list?.map((l) => l.name).join(', ') ?? '',
    },
    {
      Header: 'Tags',
      accessor: 'tags',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactTagsRenderer',
      show: true,
      valueFormatter: ({ data: { tags } }) => tags?.map((t) => t.tag).join(', ') ?? '',
    },
    {
      Header: 'Notes',
      accessor: 'notes',
      width: 200,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactNoteRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Source',
      accessor: 'source',
      width: 200,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactSourceRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'First seen',
      accessor: 'firstSeen',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    ...(listId
      ? [
          {
            Header: 'Added to list by',
            accessor: 'addedBy',
            width: 240,
            disableSortBy: true,
            disablePadding: true,
            cellRenderer: 'contactRenderer',
            show: true,
            valueFormatter: ({ value }) => value ?? '',
          },
        ]
      : []),
    {
      Header: 'Job applications',
      accessor: 'jobApplications',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactJobApplicationRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Joined talent network',
      accessor: 'talentNetworkSignup',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: true,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Signup date',
      accessor: 'signupDate',
      width: 240,
      disableSortBy: false,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Preferred job function',
      accessor: 'preferredJobFunction',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Level of experience',
      accessor: 'levelOfExperience',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Preferred location',
      accessor: 'preferredLocation',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Work environment',
      accessor: 'workEnvironment',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Job search status',
      accessor: 'jobSearchStatus',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Resume',
      accessor: 'resume',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactResumeRenderer',
      show: false,
      valueFormatter: () => 'Resume requires authentication — view in Getro',
    },
    {
      Header: 'Website',
      accessor: 'website',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactLinkRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Job alerts',
      accessor: 'jobAlerts',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    {
      Header: 'Job alerts keywords',
      accessor: 'jobAlertsKeywords',
      width: 240,
      disableSortBy: true,
      disablePadding: true,
      cellRenderer: 'contactRenderer',
      show: false,
      valueFormatter: ({ value }) => value ?? '',
    },
    ...customFieldsColumns,
  ].filter((column) => {
    if (type === 'public') {
      return !disableOneSharedList.has(column.accessor);
    }

    if (hideNewTnColumns.has(column.accessor) && !joinTnFromJB) {
      return false;
    }

    if (tnHidden) {
      return !tnHiddenColumns.has(column.accessor);
    }

    return true;
  });
};

export const useContactsTableRows = (data, { selectedRows, disabledContacts, ...options }) => {
  const history = useHistory();
  const { openSecurePdf, isLoading } = useSecurePdfOpener();
  const [selectedContacts, setSelectedContacts] = useState();

  const sortContactWorkExperiences = useCallback((contact) => {
    const experiences = [...contact.contactWorkExperiences];
    const primaryId = contact.primaryActivePosition?.id;
    const idx = experiences.findIndex((exp) => exp.id === primaryId);
    if (idx > 0) {
      const [primary] = experiences.splice(idx, 1);
      experiences.unshift(primary);
    }
    return experiences;
  }, []);

  return useMemo(
    () =>
      data?.map((contact, index) => {
        const personalEmail = contact.enrichmentPersonalEmail || contact.email;

        const sortedContactWorkExperiences = (sortContactWorkExperiences(contact) ?? []).filter((exp) => exp.active);
        const notes = contact?.notes?.length ?? 0;

        let contactUrl = `/networks/${options.activeNetwork.slug}/contacts/${contact.id}${history.location.search}`;
        let contactNotesUrl = `/networks/${options.activeNetwork.slug}/contacts/${contact.id}/notes${history.location.search}`;
        let jobApplicationsUrl = `/networks/${options.activeNetwork.slug}/contacts/${contact.id}/job-seeking${history.location.search}`;
        if (['list', 'list:add'].includes(options.page)) {
          contactUrl = `/networks/${options.activeNetwork.slug}/list/${options.list.id}/${
            options.listId ? 'contacts' : 'add-contacts'
          }/${contact.id}${history.location.search}`;
          contactNotesUrl = `/networks/${options.activeNetwork.slug}/list/${options.list.id}/${
            options.listId ? 'contacts' : 'add-contacts'
          }/${contact.id}/notes${history.location.search}`;
          jobApplicationsUrl = `/networks/${options.activeNetwork.slug}/list/${options.list.id}/${
            options.listId ? 'contacts' : 'add-contacts'
          }/${contact.id}/job-seeking${history.location.search}`;
        }

        if (options.type === 'public') {
          contactUrl = `/shared-lists/${options.list.slug}/${contact.id}${history.location.search}`;
          contactNotesUrl = `/shared-lists/${options.list.slug}/${contact.id}/notes${history.location.search}`;
          jobApplicationsUrl = `/shared-lists/${options.list.slug}/${contact.id}/job-seeking${history.location.search}`;
        }

        const row = {
          index,
          original: {
            ...contact,
            index,
          },
          options: {
            ...options,
            contactUrl,
            contactNotesUrl,
            jobApplicationsUrl,
          },
          selected: selectedRows.includes(index) || selectedRows.includes(contact.id),
          disabled: disabledContacts.includes(contact.id),
          rowId: contact.id,
          id: contact.id,
          name: contact.fullName,
          jobTitle: {
            experiences: sortedContactWorkExperiences,
            showLogo: false,
            columnKey: 'jobTitle',
          },
          company: {
            experiences: sortedContactWorkExperiences,
            showLogo: true,
            columnKey: 'companyName',
          },
          contactOf: contact?.importers ?? [],
          companySize: contact?.primaryActivePosition?.companySize
            ? reverseMapOrganizationHeadCount(contact?.primaryActivePosition?.companySize)
            : '',
          companyFunding: contact?.primaryActivePosition
            ? mapStageToFund(contact?.primaryActivePosition?.currentState)
            : '',
          linkedinProfile: contact.linkedinUrl ?? '',
          linkedinTagline: contact.linkedinTagline ?? '',
          workEmail: contact.enrichmentWorkEmails ?? [],
          personalEmail: personalEmail ? [personalEmail] : [],
          location: contact.location?.name ?? '',
          seniority: mapSeniorityType(contact?.seniority),
          addedBy: contact.addedToSharedListBy?.user?.name ?? '',
          addedDate: contact.addedToSharedListBy?.createdAt
            ? moment(contact.addedToSharedListBy?.createdAt).format('MMM DD, YYYY')
            : '',
          list: contact?.sharedList ?? [],
          tags: contact?.tags ?? [],
          notes: notes > 0 ? `${notes} ${pluralize('note', notes)}` : '',
          talentNetworkSignup: contact?.signupInfo ? 'Yes' : 'No',
          signupDate: contact?.signupInfo?.signupDate
            ? moment(contact?.signupInfo?.signupDate).format('MMM DD, YYYY')
            : '',
          firstSeen: contact?.addedAt ? moment(contact?.addedAt).format('MMM DD, YYYY') : '',
          preferredJobFunction: contact?.signupInfo?.jobFunctions?.join(', ') ?? '',
          levelOfExperience: contact?.signupInfo?.seniorities?.join(', ') ?? '',
          preferredLocation: contact?.signupInfo?.locationPreferences?.join(', ') ?? '',
          workEnvironment: contact?.signupInfo?.workEnvironments?.join(', ') ?? '',
          jobSearchStatus: contact?.signupInfo?.jobSearchStatus ?? '',
          resume: {
            url: contact?.signupInfo?.resumeUrl ?? '',
            getResume: () => {
              openSecurePdf(contact.id);
              setSelectedContacts(contact.id);
            },
            isLoading: selectedContacts === contact.id && isLoading,
          },
          jobApplications: contact?.jobApplicationsCount ?? 0,
          jobAlerts: contact?.signupInfo?.hasJobAlerts ? 'Yes' : 'No',
          jobAlertsKeywords: contact?.signupInfo?.jobAlertKeywords ?? '',
          website: contact?.signupInfo?.websiteUrl ? addProtocolToUrl(contact.websiteUrl) : '',
          industries: contact?.signupInfo?.industries?.join(', ') ?? '',
          yearsOfExperience: contact?.yearsOfExperience ? `${Math.ceil(contact.yearsOfExperience)}` : '',
          yearsInCurrentCompany: Math.ceil(contact?.primaryActivePosition?.duration ?? 0),
          industry:
            contact?.primaryActivePosition?.industryTags
              ?.map((tag) => tag.description)
              .filter((tag) => tag)
              .join(', ') ?? '',
          source: contact?.sources ?? [],
          customFields: contact?.customFields ?? [],
        };

        return row;
      }) ?? [],
    [
      data,
      sortContactWorkExperiences,
      options,
      history.location.search,
      selectedRows,
      disabledContacts,
      selectedContacts,
      isLoading,
      openSecurePdf,
    ],
  );
};

export const useSortableColumns = ({ type, listId, list, meta, activeNetwork, queryParams, customFields }) => {
  const columns = useMemo(() => {
    const sortableColumns = new Set([
      'name',
      'jobTitle',
      'location',
      'seniority',
      'company',
      'contactOf',
      'companyFunding',
      'companySize',
      'yearsOfExperience',
      'yearsInCurrentCompany',
      'signupDate',
      'firstSeen',
    ]);

    const joinTnFromJB = userSelector(jobApplicantsJobTnSelector);

    return getColumns({
      type,
      listId: type === 'public' ? listId : list?.id,
      contactCount: meta?.queryTotal ?? 0,
      tnHidden: activeNetwork.tnHidden || !activeNetwork?.features?.includes('job_board'),
      joinTnFromJB,
      customFields,
    })
      .map((column) => ({
        accessor: column.accessor,
        Header: column.Header,
      }))
      .filter((column) => sortableColumns.has(column.accessor));
  }, [type, listId, list, meta?.queryTotal, activeNetwork, customFields]);

  const initialSortBy = useMemo(() => {
    const { sortBy } = queryParams;
    const sortKey = contactsSortOptions.find((option) => option.value === sortBy);
    if (sortKey) {
      return {
        id: sortKey.field,
        desc: queryParams.sortDirection === 'desc',
      };
    }
    return {
      id: 'relevance',
      desc: true,
    };
  }, [queryParams]);

  return {
    sortableColumns: columns,
    initialSortBy,
  };
};
